import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useDebouncedCallback } from '@core/hooks/use-debounced-callback';
import { CheckCircle, XCircle, Loader2, ArrowRight, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '@core/components/ui/shared/buttons/button';
import { FloatingInput } from '@core/components/ui/shared/inputs/floating-input';
import AuthLayout from '@/components/auth-layout';
import AuthCard from '@/components/auth-card';
import PasswordStrengthBar from '@/components/password-strength-bar';
import { resetPasswordSchema, type ResetPasswordFormData } from '@/validations/schemas';
import useVerifyResetToken from '@/hooks/use-verify-reset-token';
import useCheckPassword from '@/hooks/use-check-password';
import useResetPassword from '@/hooks/use-reset-password';
import { containerVariants, itemVariants } from '@/lib/animations';

const ResetPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const token: string | null = params.get('token');
  const userId: string | null = params.get('userId');
  let returnUrl: string | null = params.get('returnUrl');

  if (!returnUrl || returnUrl === '/') {
    returnUrl = '/signIn/backend';
  }

  const [debouncedPassword, setDebouncedPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const { isLoading: isVerifying, isValid, isInvalid, isError: isVerifyError } = useVerifyResetToken(userId, token);
  const { score: passwordScore, isValid: isPasswordValid } = useCheckPassword(userId, debouncedPassword);
  const { resetPassword, isLoading: isResetting, isSuccess, error: resetError } = useResetPassword();

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const debounced = useDebouncedCallback((value: string): void => {
    setDebouncedPassword(value);
  }, 500)

  const password = form.watch('password');

  useEffect(() => {
    debounced(password);
  }, [password, debounced]);

  useEffect(() => {
    if (resetError) {
      toast.error(resetError);
    }
  }, [resetError])

  const onSubmit = async (data: ResetPasswordFormData): Promise<void> => {
    if (!isPasswordValid) {
      toast.error(t('errors.passwordTooWeak'));
      return;
    }

    if (!userId || !token) return;

    await resetPassword({
      userId,
      token,
      password: data.password,
    });
  }

  const handleRedirect = (): void => {
    if (returnUrl) {
      window.location.href = returnUrl
    } else {
      navigate('/signIn/backend')
    }
  }

  // Missing params
  if (!userId || !token) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold text-center">
              {t('errors.incompleteUrl')}
            </motion.h2>
            <motion.div variants={itemVariants}>
              <XCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-destructive" />
            </motion.div>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    )
  }

  // Loading state
  if (isVerifying) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold">
              {t('loading')}
            </motion.h2>
            <motion.div variants={itemVariants}>
              <Loader2 className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 animate-spin text-primary" />
            </motion.div>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    )
  }

  // Invalid token
  if (isInvalid) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold text-center">
              {t('errors.linkExpired')}
            </motion.h2>
            <motion.div variants={itemVariants}>
              <XCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-destructive" />
            </motion.div>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    )
  }

  // Server error
  if (isVerifyError) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold text-center">
              {t('errors.unknownServerError')}
            </motion.h2>
            <motion.div variants={itemVariants}>
              <XCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-destructive" />
            </motion.div>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    )
  }

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="flex flex-col items-center gap-4 sm:gap-6 py-4 sm:py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="p-3 sm:p-4 rounded-full bg-green-500/10"
              variants={itemVariants}
            >
              <KeyRound className="h-8 w-8 sm:h-10 sm:w-10 text-green-500" />
            </motion.div>
            <motion.h2 variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold">
              {t('passwordReset')}
            </motion.h2>
            <motion.div
              variants={itemVariants}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
            >
              <CheckCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-green-500" />
            </motion.div>
            <motion.p variants={itemVariants} className="text-sm sm:text-base text-muted-foreground text-center">
              {t('passwordResetSuccess')}
            </motion.p>
            <motion.div variants={itemVariants} className="w-full pt-2 sm:pt-4">
              <Button
                onClick={handleRedirect}
                size="lg"
                className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold group"
              >
                {t('signIn')}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    )
  }

  // Form
  if (isValid) {
    return (
      <AuthLayout>
        <AuthCard>
          <motion.div
            className="space-y-6 sm:space-y-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="text-center space-y-2">
              <motion.div
                className="flex justify-center mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              >
                <div className="p-3 sm:p-4 rounded-full bg-primary/10">
                  <KeyRound className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
                </div>
              </motion.div>
              <h1 className="text-2xl font-bold sm:text-3xl md:text-4xl">{t('resetPassword')}</h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                {t('createNewPassword')}
              </p>
            </motion.div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
              <motion.div variants={itemVariants} className="space-y-1">
                <div className="relative">
                  <Controller
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                      <FloatingInput
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        label={t('password')}
                        className="h-12 sm:h-14 text-base sm:text-lg pr-20"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={!!form.formState.errors.password}
                        readOnly={isResetting}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-20"
                    aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <PasswordStrengthBar score={passwordScore} />
                <div className="min-h-5">
                  {form.formState.errors.password && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs sm:text-sm text-destructive"
                    >
                      {t(form.formState.errors.password.message!)}
                    </motion.p>
                  )}
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-1">
                <div className="relative">
                  <Controller
                    name="confirmPassword"
                    control={form.control}
                    render={({ field }) => (
                      <FloatingInput
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        label={t('repeatPassword')}
                        className="h-12 sm:h-14 text-base sm:text-lg pr-20"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={!!form.formState.errors.confirmPassword}
                        readOnly={isResetting}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-20"
                    aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <div className="min-h-5">
                  {form.formState.errors.confirmPassword && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs sm:text-sm text-destructive"
                    >
                      {t(form.formState.errors.confirmPassword.message!)}
                    </motion.p>
                  )}
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="pt-2 sm:pt-4">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold group"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <motion.div
                      className="h-5 w-5 border-2 border-current border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : (
                    <>
                      {t('resetPassword')}
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </AuthCard>
      </AuthLayout>
    );
  }

  return null;
}

export default ResetPassword;